import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/BackButton";
import PageTitle from "../../components/PageTitle";
import useCampaignsService from "../../services/CampaignsService";
import { FormEvent, useEffect, useState } from "react";
import { Campaign, Reward } from "../../types/Campaigns";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import NegativeButton from "../../components/NegativeButton";
import PositiveButton from "../../components/PositiveButton";
import SimpleAlert from "../../components/SimpleAlert";
import TitlePicker from "../../components/Title/TitlePicker";
import RewardRulesList from "../../components/Campaigns/RewardRulesList";

export default function AdminCampaignRewardDetailPage() {
  const navigate = useNavigate();
  const { campaignId, rewardId } = useParams();
  const { getRewardsForCampaign, deleteReward, createReward, updateReward } =
    useCampaignsService();
  const [isLoading, setIsLoading] = useState(true);
  const [initiated, setInitiated] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    title: "",
    requirements: "",
    rewardCode: "",
    rewardActionType: "",
    rewardActionAndroidIntentDelivery: "",
    rewardActionAndroidIntentPackageName: "",
    rewardActionAndroidIntentActivityName: "",
    rewardActionAndroidIntentAction: "",
    rewardActionAndroidIntentCategory: "",
    rewardActionAndroidIntentFlags: "",
    rewardActionAndroidIntentDataUriTemplate: "",
    rewardActionUrlTemplate: "",
    rewardActionDisplayCodeTemplate: "",
    rewardImageUrl: "",
    image: null,
  });

  const deleteThisReward = () => {
    setIsDeleting(true);
    deleteReward(campaignId!, rewardId!)
      .then(() => {
        navigate("/admin/campaigns/" + campaignId, { replace: true });
      })
      .catch((error) => {
        console.error("Error deleting reward:", error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!formData.title) {
      errors.title = "Title is required";
    }

    if (!formData.name) {
      errors.name = "Name is required";
    }

    if (!formData.requirements) {
      errors.requirements = "Requirements are required";
    }

    if (!formData.rewardCode) {
      errors.rewardCode = "Reward code is required";
    }

    if (!formData.rewardActionType) {
      errors.rewardActionType = "Reward action type is required";
    }

    if (formData.rewardActionType === "web_url") {
      if (!formData.rewardActionUrlTemplate) {
        errors.rewardActionUrlTemplate = "Reward URL Template is required";
      }
    }

    if (formData.rewardActionType === "present_code") {
      if (!formData.rewardActionDisplayCodeTemplate) {
        errors.rewardActionDisplayCodeTemplate =
          "Reward Display Code Template is required";
      }
    }

    if (formData.rewardActionType === "android_intent") {
      if (!formData.rewardActionAndroidIntentDelivery) {
        errors.rewardActionAndroidIntentDelivery =
          "Android Intent Delivery Method is required";
      }
      if (!formData.rewardActionAndroidIntentPackageName) {
        errors.rewardActionAndroidIntentPackageName =
          "Android Intent Package Name is required";
      }
      if (!formData.rewardActionAndroidIntentActivityName) {
        errors.rewardActionAndroidIntentActivityName =
          "Android Intent Activity Name is required";
      }
    }

    // If the form is for a new article, ensure the image file is provided
    if (!formData.id && !formData.image) {
      errors.image = "Image file is required for new rewards.";
    }

    if (formData.image) {
      var imageFile = formData.image as File;
      if (!imageFile.name.match(/\.(jpg|jpeg)$/)!) {
        errors.image = "Image file must be a JPG or JPEG.";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveReward = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("title", formData.title);
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("requirements", formData.requirements);
    formDataToSubmit.append("rewardCode", formData.rewardCode);
    formDataToSubmit.append("rewardActionType", formData.rewardActionType);
    formDataToSubmit.append(
      "rewardActionAndroidIntentDelivery",
      formData.rewardActionAndroidIntentDelivery
    );
    formDataToSubmit.append(
      "rewardActionAndroidIntentPackageName",
      formData.rewardActionAndroidIntentPackageName
    );
    formDataToSubmit.append(
      "rewardActionAndroidIntentActivityName",
      formData.rewardActionAndroidIntentActivityName
    );
    formDataToSubmit.append(
      "rewardActionAndroidIntentAction",
      formData.rewardActionAndroidIntentAction
    );
    formDataToSubmit.append(
      "rewardActionAndroidIntentCategory",
      formData.rewardActionAndroidIntentCategory
    );
    formDataToSubmit.append(
      "rewardActionAndroidIntentFlags",
      formData.rewardActionAndroidIntentFlags
    );
    formDataToSubmit.append(
      "rewardActionAndroidIntentDataUriTemplate",
      formData.rewardActionAndroidIntentDataUriTemplate
    );
    formDataToSubmit.append(
      "rewardActionUrlTemplate",
      formData.rewardActionUrlTemplate
    );
    formDataToSubmit.append(
      "rewardActionDisplayCodeTemplate",
      formData.rewardActionDisplayCodeTemplate
    );

    // Load the file as a blob.
    if (formData.image) {
      const imageBlob = await new Promise<Blob>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const blob = new Blob([reader.result as ArrayBuffer]);
          resolve(blob);
        };
        reader.readAsArrayBuffer(formData.image!);
      });
      formDataToSubmit.append("Image", imageBlob);
    }

    var savedReward: Reward | null = null;

    if (formData.id) {
      var updateResult = await updateReward(
        campaignId!,
        formData.id,
        formDataToSubmit
      );

      savedReward = updateResult as Reward;

      // If the result is a http error
      if (!savedReward) {
        alert("Error creating reward");
        setIsSaving(false);
        return;
      }

      
    } else {
      var createResult = await createReward(campaignId!, formDataToSubmit);
      savedReward = createResult as Reward;

      if (!saveReward) {
        alert("Error creating reward");
        setIsSaving(false);
        return;
      }
    }

    setFormData({
      ...formData,
      id: savedReward.id,
      rewardImageUrl: savedReward.rewardImageUrl!,
    });

    setIsClean(true);
    setIsSaving(false);

    const cameFromNew = rewardId?.toLowerCase() === "new";

    if (cameFromNew) {
      navigate(`/admin/campaigns/${campaignId!}/rewards/${savedReward.id}`, {
        replace: true,
      });
    }
  };

  const isFormValid = submitted && Object.keys(validationErrors).length === 0;

  const validationError = (fieldName: string): null | string => {
    return validationErrors[fieldName] || null;
  };

  const validStyle = (fieldName: string) => {
    // Find the validationErrors key with the fieldName.
    let valid = Object.keys(validationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const handleImageFileChange = (e: any) => {
    const { files } = e.target;

    setIsClean(false);

    if (!files || files.length === 0) {
      setFormData({
        ...formData,
        image: null,
      });

      setPreviewBlob(null);

      return;
    }

    setFormData({
      ...formData,
      image: files[0],
    });

    // Load the image and set the preview blob.
    const reader = new FileReader();
    reader.onload = () => {
      const blob = new Blob([reader.result as ArrayBuffer]);
      setPreviewBlob(blob);
    };
    reader.readAsArrayBuffer(files[0]);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (initiated) {
      return;
    }

    setIsLoading(true);
    setInitiated(true);

    if (rewardId?.toLowerCase() == "new") {
      setIsLoading(false);
    } else {
      getRewardsForCampaign(campaignId as string)
        .then((result) => {
          var rewards = result as Reward[];

          var reward = rewards.find((c) => c.id.toString() === rewardId)!;

          setFormData({
            id: reward.id,
            title: reward.title,
            name: reward.name,
            requirements: reward.requirements || "",
            rewardCode: reward.rewardCode,
            rewardActionType: reward.rewardActionType,
            rewardActionAndroidIntentDelivery:
              reward.rewardActionAndroidIntentDelivery || "",
            rewardActionAndroidIntentPackageName:
              reward.rewardActionAndroidIntentPackageName || "",
            rewardActionAndroidIntentActivityName:
              reward.rewardActionAndroidIntentActivityName || "",
            rewardActionAndroidIntentAction:
              reward.rewardActionAndroidIntentAction || "",
            rewardActionAndroidIntentCategory:
              reward.rewardActionAndroidIntentCategory || "",
            rewardActionAndroidIntentFlags:
              reward.rewardActionAndroidIntentFlags || "",
            rewardActionAndroidIntentDataUriTemplate:
              reward.rewardActionAndroidIntentDataUriTemplate || "",
            rewardActionUrlTemplate: reward.rewardActionUrlTemplate || "",
            rewardActionDisplayCodeTemplate:
              reward.rewardActionDisplayCodeTemplate || "",
            rewardImageUrl: reward.rewardImageUrl || "",
            image: null,
          });
        })
        .catch((error) => {
          console.error("Error fetching reward:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [initiated]);

  return (
    <>
      <div
        className={`grid gap-x-6 gap-y-8 grid-cols-1 xl:grid-cols-3 2xl:grid-cols-4 mb-8`}
      >
        <section>
          <div className="flex flex-row">
            <BackButton />
            <PageTitle className="text-4xl">
              {isLoading
                ? "Loading"
                : formData.id
                ? formData.title
                : "New Reward"}
              {isLoading && <LoadingEllipsis />}
            </PageTitle>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-2 col-span-2">
              {isLoading && (
                <Ghost additionalClasses="h-24 col-span-2 rounded" />
              )}
              {!isLoading && formData && (
                <form onSubmit={saveReward} className="form">
                  <div>
                    <div className="card mt-6 p-8 flex flex-col gap-4">
                      <div className="">
                        <label htmlFor="name" className="block leading-6">
                          Reward name (internal use) *
                        </label>

                        <input
                          disabled={isSaving}
                          type="text"
                          name="name"
                          placeholder="Reward name "
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "name"
                          )}`}
                        />

                        {validationError("name") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("name")}
                          </div>
                        )}
                      </div>
                      <div className="">
                        {formData.rewardImageUrl && !previewBlob && (
                          <div className="flex flex-col gap-2">
                            <img
                              src={formData.rewardImageUrl}
                              alt="Reward"
                              className="h-32 w-32 object-contain"
                            />
                          </div>
                        )}
                        {previewBlob && (
                          <div className="flex flex-col gap-2">
                            <img
                              src={URL.createObjectURL(previewBlob)}
                              alt="Reward"
                              className="h-32 w-32 object-contain"
                            />
                          </div>
                        )}
                        <label htmlFor="image" className="block leading-6 pt-4">
                          Reward image (JPG or JPEG) *
                        </label>

                        <input
                          disabled={isSaving}
                          accept="image/jpg,image/jpeg"
                          type="file"
                          name="image"
                          onChange={handleImageFileChange}
                          className={`w-full px-3 py-2 border rounded text-white ${validStyle(
                            "image"
                          )}`}
                        />

                        {validationError("image") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("image")}
                          </div>
                        )}
                      </div>
                      <div className="">
                        <label htmlFor="title" className="block leading-6">
                          Reward title (shown in app) *
                        </label>

                        <input
                          disabled={isSaving}
                          type="text"
                          name="title"
                          placeholder="Reward title *"
                          value={formData.title}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "title"
                          )}`}
                        />

                        {validationError("title") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("title")}
                          </div>
                        )}
                      </div>
                      <div className="">
                        <label
                          htmlFor="requirements"
                          className="block leading-6"
                        >
                          Reward requirements (shown as instructions in app) *
                        </label>

                        <input
                          disabled={isSaving}
                          type="text"
                          name="requirements"
                          placeholder="Reward requirements *"
                          value={formData.requirements}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "requirements"
                          )}`}
                        />

                        {validationError("requirements") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("requirements")}
                          </div>
                        )}
                      </div>
                      <div className="">
                        <label htmlFor="rewardCode" className="block leading-6">
                          Reward code *
                        </label>

                        <input
                          disabled={isSaving}
                          type="text"
                          name="rewardCode"
                          placeholder="Reward code *"
                          value={formData.rewardCode}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "rewardCode"
                          )}`}
                        />

                        {validationError("rewardCode") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("rewardCode")}
                          </div>
                        )}
                      </div>
                      <div className="">
                        <label
                          htmlFor="rewardActionType"
                          className="block leading-6"
                        >
                          Reward action type *
                        </label>

                        <select
                          disabled={isSaving}
                          name="rewardActionType"
                          value={formData.rewardActionType}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "rewardActionType"
                          )}`}
                        >
                          <option value="">Select action type</option>
                          <option value="present_code">
                            Present code in app
                          </option>
                          <option value="android_intent">
                            Run Android intent
                          </option>
                          <option value="web_url">Open web URL</option>
                        </select>

                        {validationError("rewardActionType") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("rewardActionType")}
                          </div>
                        )}
                      </div>
                      {formData.rewardActionType === "web_url" && (
                        <>
                          <div className="">
                            <label
                              htmlFor="rewardActionUrlTemplate"
                              className="block leading-6"
                            >
                              Reward URL Template *
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionUrlTemplate"
                              placeholder="Reward URL Template. Use {code} for the reward code."
                              value={formData.rewardActionUrlTemplate}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionUrlTemplate"
                              )}`}
                            />

                            {validationError("rewardActionUrlTemplate") && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError("rewardActionUrlTemplate")}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {formData.rewardActionType === "present_code" && (
                        <>
                          <div className="">
                            <label
                              htmlFor="rewardActionDisplayCodeTemplate"
                              className="block leading-6"
                            >
                              Reward Display Code Template *
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionDisplayCodeTemplate"
                              placeholder="Reward Display Code Template. Use {code} for the reward code."
                              value={formData.rewardActionDisplayCodeTemplate}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionDisplayCodeTemplate"
                              )}`}
                            />

                            {validationError(
                              "rewardActionDisplayCodeTemplate"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionDisplayCodeTemplate"
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {formData.rewardActionType === "android_intent" && (
                        <>
                          <div className="">
                            <label
                              htmlFor="rewardACtionAndroidIntentDelivery"
                              className="block leading-6"
                            >
                              Android Intent Delivery Method *
                            </label>
                            <select
                              disabled={isSaving}
                              name="rewardActionAndroidIntentDelivery"
                              value={formData.rewardActionAndroidIntentDelivery}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentDelivery"
                              )}`}
                            >
                              <option value="">Select delivery method</option>
                              <option value="start_activity">
                                Start an activity
                              </option>
                              <option value="broadcast">
                                Send a broadcast
                              </option>
                              <option value="service">Start a service</option>
                            </select>
                          </div>
                          <div className="">
                            <label
                              htmlFor="rewardActionAndroidIntentPackageName"
                              className="block leading-6"
                            >
                              Android Intent Package Name *
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionAndroidIntentPackageName"
                              placeholder="Package name (e.g. com.beam.beamxr)"
                              value={
                                formData.rewardActionAndroidIntentPackageName
                              }
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentPackageName"
                              )}`}
                            />

                            {validationError(
                              "rewardActionAndroidIntentPackageName"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionAndroidIntentPackageName"
                                )}
                              </div>
                            )}
                          </div>
                          <div className="">
                            <label
                              htmlFor="rewardActionAndroidIntentActivityName"
                              className="block leading-6"
                            >
                              Android Intent Activity Name *
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionAndroidIntentActivityName"
                              placeholder="Activity name (e.g. com.beam.beamxr.MainActivity)"
                              value={
                                formData.rewardActionAndroidIntentActivityName
                              }
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentActivityName"
                              )}`}
                            />

                            {validationError(
                              "rewardActionAndroidIntentActivityName"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionAndroidIntentActivityName"
                                )}
                              </div>
                            )}
                          </div>
                          <div className="">
                            <label
                              htmlFor="RewardActionAndroidIntentAction"
                              className="block leading-6"
                            >
                              Android Intent Action
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionAndroidIntentAction"
                              placeholder="Action (e.g. android.intent.action.VIEW)"
                              value={formData.rewardActionAndroidIntentAction}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentAction"
                              )}`}
                            />

                            {validationError(
                              "rewardActionAndroidIntentAction"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionAndroidIntentAction"
                                )}
                              </div>
                            )}
                          </div>
                          <div className="">
                            <label
                              htmlFor="RewardActionAndroidIntentCategory"
                              className="block leading-6"
                            >
                              Android Intent Category
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionAndroidIntentCategory"
                              placeholder="Category (e.g. android.intent.category.DEFAULT)"
                              value={formData.rewardActionAndroidIntentCategory}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentCategory"
                              )}`}
                            />

                            {validationError(
                              "rewardActionAndroidIntentCategory"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionAndroidIntentCategory"
                                )}
                              </div>
                            )}
                          </div>
                          <div className="">
                            <label
                              htmlFor="RewardActionAndroidIntentDataUriTemplate"
                              className="block leading-6"
                            >
                              Android Intent Data URI Template
                            </label>
                            <input
                              disabled={isSaving}
                              type="text"
                              name="rewardActionAndroidIntentDataUriTemplate"
                              placeholder="game://beam/unlock?achievementcode={achievement.code}&rewardcode={reward.code}"
                              value={formData.rewardActionAndroidIntentDataUriTemplate}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentDataUriTemplate"
                              )}`}
                            />

                            {validationError(
                              "rewardActionAndroidIntentDataUriTemplate"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionAndroidIntentDataUriTemplate"
                                )}
                              </div>
                            )}
                          </div>
                          <div className="">
                            <label
                              htmlFor="RewardActionAndroidIntentFlags"
                              className="block leading-6"
                            >
                              Android Intent Flags
                            </label>
                            <input
                              disabled={isSaving}
                              type="number"
                              name="rewardActionAndroidIntentFlags"
                              placeholder="Flags (e.g. 0x10000000)"
                              value={formData.rewardActionAndroidIntentFlags}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                                "rewardActionAndroidIntentFlags"
                              )}`}
                            />

                            {validationError(
                              "rewardActionAndroidIntentFlags"
                            ) && (
                              <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                                {validationError(
                                  "rewardActionAndroidIntentFlags"
                                )}
                              </div>
                            )}
                          </div>
                          
                        </>
                      )}
                      <div className="sm:col-span-4 flex items-center gap-2 justify-end mt-4">
                        {formData.id && (
                          <NegativeButton
                            type="button"
                            disabled={isSaving}
                            className={`text-red-500 py-2 px-4 rounded border-red-900 border hover:bg-red-900/25 `}
                            onClick={() => setDeleteAlertOpen(true)}
                          >
                            Delete reward
                          </NegativeButton>
                        )}
                        <PositiveButton
                          type="submit"
                          disabled={isSaving || isClean}
                        >
                          {isSaving
                            ? "Saving..."
                            : formData.id && isClean
                            ? "Saved"
                            : "Save changes"}
                        </PositiveButton>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
        {formData.id ? (
          <section className="col-span-1 xl:col-span-2 2xl:col-span-3">
            <RewardRulesList campaignId={campaignId!} rewardId={rewardId!} />
          </section>
        ) : <section className="col-span-1 xl:col-span-2 2xl:col-span-3">
            <h4 className="text-2xl mb-4">Reward Rules</h4>
            <p className="text-gray-400">Save the reward to manage its rules.</p>
          </section>}
      </div>
      <SimpleAlert
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        title={`Are you sure you want to delete this reward?`}
        message="This action cannot be undone and it will destroy all associated progress for users!"
        confirmText={`Delete ${formData.title}`}
        onConfirm={deleteThisReward}
      />
    </>
  );
}
